import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Tooltip,
  Checkbox,
  Button,
  InputAdornment,
  TextField,
  Chip,
  Pagination,
  useTheme,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  getCaseTemplate,
  getPracticeArea,
} from "../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { backend_api } from "../../../static/server";
import { CloseRounded, SearchRounded } from "@mui/icons-material";
import {
  appendTeamMemberLawyerId,
  debounce,
  pageScrollStyle,
} from "../../../styles/styles";
import TemplateSkeleton from "../../TemplateSkeleton";
import { useDialog } from "../../DialogContext";
import MatterForm from "./MatterForm";
import axios from "axios";
import { toast } from "react-toastify";
import noDataFound from "../../../assets/no-data-found.webp";

const DrawerTemplate = ({
  mode = "create",
  onTemplateSelected,
  onMatterCreated,
}) => {
  const { showDialog, closeDialog } = useDialog();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { token, teamMemberLawyerId } = useSelector(
    (state) => state.user,
  );
  const {
    allPracticeArea,
    allCaseTemplate: storeCaseTemplates,
    loading: isCaseLoading,
    error: caseError,
    caseTemplatePagination,
  } = useSelector((state) => state.case);
  const [allCaseTemplate, setAllCaseTemplate] = useState([]);
  const { page = 1, pageSize = 10, count = 0 } = caseTemplatePagination || {};
  const [duplicateLoad, setDuplicateLoad] = useState(false);

  const [initialLoading, setInitialLoading] = useState(true);
  const loading = isCaseLoading;
  const totalPages = pageSize > 0 ? Math.ceil(count / pageSize) : 0;

  useEffect(() => {
    if (token) {
      dispatch(
        getCaseTemplate({
          url: `${backend_api}case/template-register/`,
          token: token,
          teamMemberLawyerId,
        }),
      );
    }
  }, [dispatch, token, teamMemberLawyerId]);

  const debouncedFetchCasesTemplate = useCallback(
    debounce((query) => {
      const searchParam = query ? `name=${encodeURIComponent(query)}` : "";
      const url = query
        ? `${backend_api}case/template-register/?${searchParam}`
        : `${backend_api}case/template-register/`;

      dispatch(
        getCaseTemplate({
          url: url,
          token,
          teamMemberLawyerId,
        }),
      );
    }, 500),
    [dispatch, backend_api, token],
  );

  const handleCaseSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedFetchCasesTemplate(value);
  };

  const handlePageChange = (e, value) => {
    e.preventDefault();
    setSelectedTemplate(null); // Reset selection on page change
    dispatch(
      getCaseTemplate({
        url: `${backend_api}case/template-register/`,
        token: token,
        teamMemberLawyerId,
        page: value,
        pageSize: pageSize,
      }),
    );
  };

  const getPracticeAreaName = (area) => {
    if (!area) return "General";
    if (typeof area === "object" && area.name) return area.name;
    const found = allPracticeArea?.find((p) => p.id === area);
    return found?.name || "General";
  };

  useEffect(() => {
    if (Array.isArray(storeCaseTemplates)) {
      setAllCaseTemplate(storeCaseTemplates.slice());
    } else {
      setAllCaseTemplate([]);
    }
  }, [storeCaseTemplates]);

  useEffect(() => {
    if (loading) {
      setInitialLoading(true);
    } else if (storeCaseTemplates !== null || caseError) {
      setInitialLoading(false);
    }
  }, [loading, storeCaseTemplates, caseError]);

  useEffect(() => {
    if (
      selectedTemplate &&
      !allCaseTemplate.some((template) => template.id === selectedTemplate)
    ) {
      setSelectedTemplate(null);
    }
  }, [allCaseTemplate, selectedTemplate]);

  useEffect(() => {
    if (token) {
      dispatch(
        getPracticeArea({
          url: `${backend_api}case/case-type/?case_service_type=practice_area`,
          token: token,
        }),
      );
    }
  }, [dispatch, token]);

  const duplicateTemplateData = async (template) => {
    setDuplicateLoad(true);
    try {
      const res = await axios.post(
        appendTeamMemberLawyerId(
          `${backend_api}case/template-register/${template?.id}/duplicate/`,
          teamMemberLawyerId,
        ),
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        },
      );
      return res?.data;
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.detail || "Failed to duplicate matter template",
      );
      return null;
    } finally {
      setDuplicateLoad(false);
    }
  };

  const handleTemplateSelection = (templateId) => {
    setSelectedTemplate(templateId);
  };

  const handleApply = async () => {
    if (!selectedTemplate) return;

    const selectedTemplateData = allCaseTemplate?.find(
      (template) => template.id === selectedTemplate,
    );

    if (!selectedTemplateData) return;

    let templateToUse = selectedTemplateData;

    if (selectedTemplateData?.is_standard) {
      const duplicate = await duplicateTemplateData(selectedTemplateData);
      if (duplicate) {
        templateToUse = duplicate;
      } else {
        return;
      }
    }
    closeDialog();

    if (typeof onTemplateSelected === "function") {
      onTemplateSelected(templateToUse);
      return;
    }

    showDialog(
      <MatterForm
        formData={templateToUse}
        onClose={() => {}}
        onSubmitSuccess={onMatterCreated}
      />,
      {
        dialogTitle: "Preview Form",
        fullScreen: true,
        width: "100%",
        formType: "matter",
      },
    );
  };

  // No Data Image Component
  const NoDataImage = () => (
    <Box sx={{ height: "8rem", width: "8rem", mx: "auto", mb: 2 }}>
      <img
        src={noDataFound}
        alt="No Data"
        style={{
          height: "100%",
          width: "100%",
          objectFit: "contain",
          opacity: 0.6,
        }}
        onError={(e) => {
          e.target.onerror = null;
          // Fallback to a simple SVG if the placeholder fails
          e.target.src = { noDataFound };
        }}
      />
    </Box>
  );

  // Show initial loading skeleton
  // if (initialLoading) {
  //   return (
  //     <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>

  //     </Box>
  //   );
  // }

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: "80vh",
        overflow: "auto",
        position: "relative",
        ...pageScrollStyle,
      }}
      // onScroll={handleScroll}
    >
      {/* Search Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "0.5rem",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: "0rem",
          p: "0.5rem",
          background: "#FFFFFF95",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          zIndex: 100,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search templates..."
          value={searchTerm}
          onChange={handleCaseSearch}
          sx={{
            minWidth: "240px",
            fontSize: "0.8rem",
            "& .MuiOutlinedInput-root": {
              borderRadius: "32px",
              pr: 0.5,
              backgroundColor: "#f8f9fa",
            },
            "& .MuiInputBase-input::placeholder": {
              opacity: 0.5,
              fontSize: "0.8rem",
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchTerm ? (
                  <IconButton
                    size="small"
                    onClick={() => handleCaseSearch({ target: { value: "" } })}
                    sx={{
                      border: "0.1px solid #e5e5e5",
                      p: 0.5,
                      backgroundColor: "white",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <CloseRounded fontSize="small" />
                  </IconButton>
                ) : (
                  <IconButton
                    size="small"
                    disabled
                    sx={{
                      border: "0.1px solid #e5e5e5",
                      p: 0.5,
                      backgroundColor: "white",
                    }}
                  >
                    <SearchRounded fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {initialLoading ? (
        <TemplateSkeleton />
      ) : (
        <Box sx={{ py: "0.5rem", px: "1rem", minHeight: "20rem" }}>
          {allCaseTemplate && allCaseTemplate.length > 0 ? (
            <Grid container spacing={1.5}>
              {allCaseTemplate.map((item, i) => {
                const isSelected = selectedTemplate === item.id;
                return (
                  <Grid key={`${item.id}-${i}`} item xs={12}>
                    <Box
                      sx={{
                        borderRadius: "1rem",
                        border: `1px solid ${
                          isSelected
                            ? theme.palette.primary.main
                            : theme.palette.divider
                        }`,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "pointer",
                        position: "relative",
                        backgroundColor: isSelected
                          ? theme.palette.action.selected
                          : theme.palette.background.paper,
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          transform: "translateY(-1px)",
                          boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                        },
                        ...(isSelected && {
                          boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`,
                        }),
                      }}
                      onClick={() => handleTemplateSelection(item.id)}
                    >
                      <Box sx={{ p: "1rem" }}>
                        {/* Header */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flex: 1,
                            }}
                          >
                            <Checkbox
                              checked={isSelected}
                              size="small"
                              sx={{
                                p: 0.5,
                                mr: 1,
                              }}
                            />
                            <Tooltip title={item?.template_name || "-"} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.9rem",
                                  fontWeight: isSelected ? 600 : 500,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  color: isSelected
                                    ? "primary.main"
                                    : "#2c2c2c",
                                  letterSpacing: "-0.01em",
                                }}
                              >
                                {item?.template_name || "-"}
                              </Typography>
                            </Tooltip>
                          </Box>
                          <Box>
                            {item?.is_standard && (
                              <Chip
                                label="Standard"
                                size="small"
                                sx={{
                                  fontSize: "0.7rem",
                                  height: "1.5rem",
                                  backgroundColor: "primary.main",
                                  color: "white",
                                  fontWeight: 500,
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Description */}
                        {item?.template_description && (
                          <Typography
                            sx={{
                              color: isSelected ? "#2c2c2c" : "#666666",
                              fontSize: "0.8rem",
                              lineHeight: 1.5,
                              mb: 1.5,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {item.template_description}
                          </Typography>
                        )}

                        {/* Practice Area */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mt: 1,
                            pt: 1,
                            borderTop: "1px solid #f0f0f0",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#888888",
                              fontSize: "0.75rem",
                              fontWeight: 500,
                            }}
                          >
                            Practice Area:
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              ml: 0.5,
                              color: isSelected ? "primary.main" : "#2c2c2c",
                            }}
                          >
                            {getPracticeAreaName(item?.practice_area)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "20rem",
                flexDirection: "column",
              }}
            >
              <NoDataImage />
              <Typography
                sx={{
                  textAlign: "center",
                  color: "#666666",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  mb: 0.5,
                }}
              >
                {searchTerm
                  ? "No templates found matching your search"
                  : "No templates available"}
              </Typography>
              {searchTerm && (
                <Typography
                  sx={{
                    textAlign: "center",
                    color: "#999999",
                    fontSize: "0.8rem",
                  }}
                >
                  Try adjusting your search terms
                </Typography>
              )}
            </Box>
          )}

          {/* Loading indicator for infinite scroll */}
          {loading && !initialLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <TemplateSkeleton />
            </Box>
          )}
        </Box>
      )}

      {/* Bottom Bar with Pagination and Apply Button */}
      <Box
        sx={{
          position: "sticky",
          bottom: "0rem",
          background: "#FFFFFF95",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          zIndex: 100,
          p: "0.75rem 1rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid #f0f0f0",
          gap: 2,
        }}
      >
        {/* Pagination */}
        {totalPages > 0 && (
          <Pagination
            page={page}
            count={totalPages}
            variant="outlined"
            color="primary"
            size="small"
            onChange={handlePageChange}
          />
        )}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Results Count */}
        {count > 0 && (
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: "#666666",
              fontWeight: 500,
            }}
          >
            {count} template{count !== 1 ? "s" : ""} found
          </Typography>
        )}

        {/* Apply Button */}
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!selectedTemplate || loading || duplicateLoad}
          size="small"
        >
          {duplicateLoad
            ? "Preparing template..."
            : mode === "import"
              ? "Use Template for Import"
              : "Apply & Open Form"}
        </Button>
      </Box>
    </Box>
  );
};

export default DrawerTemplate;
